import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Container, Grid, Typography, Button, Card, CardContent,
  Chip, Avatar, Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DevicesIcon from '@mui/icons-material/Devices';
import ForestIcon from '@mui/icons-material/Forest';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { Link } from 'react-router-dom';

// ── Hero carousel images (local assets copied from alianzafront) ──
import hero1 from '../assets/hero1.webp';
import hero2 from '../assets/hero2.webp';
import hero3 from '../assets/hero3.webp';
import hero4 from '../assets/hero4.webp';

const carouselSlides = [
  {
    image: hero1,
    badge: 'Red Agroindustrial de Colombia',
    title: 'El Campo',
    titleAccent: 'Colombiano',
    titleEnd: 'Merece Más',
    subtitle: 'FENAGRO APL transforma el agro a través de la solidaridad, innovación y fortalecimiento colectivo de sus asociados.',
  },
  {
    image: hero2,
    badge: 'Economía Solidaria & Colaborativa',
    title: 'Unidos',
    titleAccent: 'Crecemos',
    titleEnd: 'Juntos',
    subtitle: 'Una red empresarial de segundo nivel que articula productores, emprendedores y comunidades para generar bienestar real.',
  },
  {
    image: hero3,
    badge: 'Banco de Proyectos · Mercado Asegurado',
    title: 'Del Campo',
    titleAccent: 'a la Mesa',
    titleEnd: 'con Equidad',
    subtitle: 'Garantizamos el mercado asegurado de sus productos antes de sembrar, eliminando la intermediación abusiva.',
  },
  {
    image: hero4,
    badge: 'Escuelas Agroindustriales · BPA · APL',
    title: 'Formación',
    titleAccent: 'Tecnología',
    titleEnd: 'y Progreso',
    subtitle: 'Educamos, tecnificamos y conectamos a nuestros asociados con los estándares de producción limpia más exigentes.',
  },
];

const services = [
  { icon: <RocketLaunchIcon />, title: 'Gestión de Emprendimiento', desc: 'Formulación de planes de negocio, unidades productivas y acompañamiento especializado.', path: '/banco-emprendimiento', color: '#2e7d32' },
  { icon: <AccountBalanceIcon />, title: 'Banco de Proyectos', desc: 'Apalancamiento y cofinanciación con alcaldías, gobernaciones y entidades privadas.', path: '/banco-proyectos', color: '#388e3c' },
  { icon: <LightbulbIcon />, title: 'Gestión del Conocimiento', desc: 'Ciclo PHVA, potenciación del Know-How y administración eficiente de procesos.', path: '/banco-innovacion', color: '#43a047' },
  { icon: <GroupsIcon />, title: 'Redes Empresariales', desc: 'Fortalecimiento del tejido empresarial solidario a través de alianzas estratégicas.', path: '/redes-empresariales', color: '#1b5e20' },
  { icon: <StorefrontIcon />, title: 'Mercado Asegurado', desc: 'Comercialización con equidad. Conexión directa productor-consumidor sin intermediarios.', path: '/mercado-asegurado', color: '#558b2f' },
  { icon: <SchoolIcon />, title: 'Escuelas Agroindustriales', desc: 'Formación en emprendimiento agro-industrial con protocolos de producción limpia APL.', path: '/escuelas-agroindustriales', color: '#33691e' },
  { icon: <BusinessCenterIcon />, title: 'Fortalecimiento Empresarial', desc: 'Consultoría especializada en economía solidaria y gerencia agro-industrial.', path: '/bienes-servicios', color: '#689f38' },
  { icon: <GavelIcon />, title: 'Asesoría Jurídica', desc: 'Constitución de sociedades, contratos, registro mercantil y consultoría en marcas.', path: '/bienes-servicios', color: '#827717' },
  { icon: <TrendingUpIcon />, title: 'Marketing Digital', desc: 'Estrategias digitales, branding, CRM y medición de retorno de inversión.', path: '/bienes-servicios', color: '#e65100' },
  { icon: <DevicesIcon />, title: 'Asesoría TIC', desc: 'ERP, ciberseguridad, e-commerce, Business Intelligence y soluciones tecnológicas.', path: '/bienes-servicios', color: '#0277bd' },
];

const stats = [
  { value: '2016', label: 'Año de Fundación', suffix: '' },
  { value: '4', label: 'Pilares de Acción', suffix: '' },
  { value: '100', label: 'Asociados Activos', suffix: '+' },
  { value: 'II', label: 'Nivel Federativo', suffix: '' },
];

function AnimatedCounter({ value, suffix, active }) {
  const [display, setDisplay] = useState(0);
  const isNumeric = !isNaN(parseInt(value));
  useEffect(() => {
    if (!active || !isNumeric) { setDisplay(value); return; }
    const end = parseInt(value);
    const start = Date.now();
    const timer = setInterval(() => {
      const p = Math.min((Date.now() - start) / 1400, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(eased * end));
      if (p >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [active, value, isNumeric]);
  return <>{isNumeric ? display : display}{suffix}</>;
}

export default function Home() {
  const [slideIndex, setSlideIndex] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);
  const total = carouselSlides.length;

  // Auto-advance carousel
  useEffect(() => {
    const id = setInterval(() => setSlideIndex(i => (i + 1) % total), 5000);
    return () => clearInterval(id);
  }, [total]);

  // IntersectionObserver for stats counter
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box sx={{ background: '#ffffff' }}>

      {/* ═══════════════════════════════════════════
          HERO — full-screen photo carousel
      ═══════════════════════════════════════════ */}
      <Box sx={{
        position: 'relative',
        height: '100vh',
        minHeight: 600,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Slide backgrounds */}
        {carouselSlides.map((slide, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute', inset: 0,
              backgroundImage: `url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: slideIndex === i ? 1 : 0,
              transition: 'opacity 1.4s ease-in-out',
              zIndex: 0,
            }}
          />
        ))}

        {/* Light gradient overlay — bright at bottom, subtle on top */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: `
            linear-gradient(
              to bottom,
              rgba(0,0,0,0.30) 0%,
              rgba(0,0,0,0.15) 40%,
              rgba(10,40,15,0.45) 100%
            )
          `,
        }} />

        {/* Carousel dot indicators */}
        <Box sx={{
          position: 'absolute', bottom: 40, left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', gap: 1.5, zIndex: 3,
        }}>
          {carouselSlides.map((_, i) => (
            <Box
              key={i}
              onClick={() => setSlideIndex(i)}
              sx={{
                width: slideIndex === i ? 36 : 10,
                height: 10,
                borderRadius: 5,
                bgcolor: slideIndex === i ? '#ffffff' : 'rgba(255,255,255,0.45)',
                cursor: 'pointer',
                transition: 'all 0.35s ease',
                boxShadow: slideIndex === i ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
              }}
            />
          ))}
        </Box>

        {/* Slide content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: 720 }}>

            {/* ── Texto animado: desliza horizontalmente, botones no tocan esto ── */}
            <Box sx={{ overflow: 'hidden', mb: 4 }}>
              <Box
                key={slideIndex}
                sx={{
                  animation: 'slideInRight 0.55s cubic-bezier(0.25,0.46,0.45,0.94) both',
                  '@keyframes slideInRight': {
                    from: { opacity: 0, transform: 'translateX(50px)' },
                    to:   { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
              >
                {/* Badge */}
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1,
                  px: 2, py: 0.7, mb: 3,
                  background: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.45)',
                  borderRadius: 20,
                  backdropFilter: 'blur(8px)',
                }}>
                  <ForestIcon sx={{ color: '#a5d6a7', fontSize: 15 }} />
                  <Typography sx={{
                    fontSize: '0.72rem', letterSpacing: '0.14em',
                    color: 'rgba(255,255,255,0.95)', textTransform: 'uppercase',
                    fontFamily: "'Outfit', sans-serif", fontWeight: 600,
                  }}>
                    {carouselSlides[slideIndex].badge}
                  </Typography>
                </Box>

                {/* Headline */}
                <Typography
                  variant="h1"
                  sx={{
                    color: '#ffffff',
                    fontSize: { xs: '2.6rem', md: '4rem', lg: '5.2rem' },
                    lineHeight: 1.06, mb: 0,
                    textShadow: '0 3px 20px rgba(0,0,0,0.35)',
                  }}
                >
                  {carouselSlides[slideIndex].title}
                  <Box component="span" sx={{
                    display: 'block', color: '#a5d6a7',
                    textShadow: '0 3px 20px rgba(0,0,0,0.3)',
                  }}>
                    {carouselSlides[slideIndex].titleAccent}
                  </Box>
                  {carouselSlides[slideIndex].titleEnd}
                </Typography>

                {/* Subtitle */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255,255,255,0.88)',
                    mt: 2.5, mb: 0,
                    fontSize: '1.08rem', lineHeight: 1.75, maxWidth: 560,
                    textShadow: '0 1px 8px rgba(0,0,0,0.4)',
                  }}
                >
                  {carouselSlides[slideIndex].subtitle}
                </Typography>
              </Box>
            </Box>

            {/* ── Botones estáticos — nunca se animan, siempre visibles ── */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/nosotros"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                  color: '#ffffff',
                  fontSize: '0.85rem', letterSpacing: '0.06em',
                  px: 4, py: 1.6, borderRadius: 3,
                  boxShadow: '0 6px 24px rgba(46,125,50,0.45)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 32px rgba(46,125,50,0.55)',
                  },
                }}
              >
                Conocer FENAGRO
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/afiliacion"
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)', color: '#ffffff',
                  fontSize: '0.85rem', letterSpacing: '0.06em', px: 4, py: 1.6,
                  borderWidth: 2, borderRadius: 3,
                  backdropFilter: 'blur(6px)',
                  background: 'rgba(255,255,255,0.10)',
                  '&:hover': {
                    borderColor: '#a5d6a7', color: '#a5d6a7',
                    background: 'rgba(255,255,255,0.16)',
                    borderWidth: 2,
                  },
                }}
              >
                Afiliarme
              </Button>
            </Box>

          </Box>
        </Container>

        {/* Scroll hint arrow */}
        <Box sx={{
          position: 'absolute', bottom: 22, right: 40, zIndex: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
          animation: 'bob 2.2s ease-in-out infinite',
          '@keyframes bob': {
            '0%,100%': { transform: 'translateY(0)' },
            '50%': { transform: 'translateY(-8px)' },
          },
        }}>
          <Typography sx={{ fontSize: '0.6rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' }}>
            scroll
          </Typography>
          <Box sx={{ width: 1.5, height: 36, background: 'linear-gradient(to bottom, rgba(255,255,255,0.5), transparent)', borderRadius: 1 }} />
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════
          STATS BAR — light, airy green
      ═══════════════════════════════════════════ */}
      <Box
        ref={statsRef}
        sx={{
          background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 50%, #e8f5e9 100%)',
          borderBottom: '1px solid rgba(46,125,50,0.12)',
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s, i) => (
              <Grid item xs={6} md={3} key={i} sx={{ textAlign: 'center' }}>
                <Typography sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: { xs: '2.2rem', md: '3.2rem' },
                  fontWeight: 900, color: '#2e7d32', lineHeight: 1,
                }}>
                  <AnimatedCounter value={s.value} suffix={s.suffix} active={statsVisible} />
                </Typography>
                <Typography sx={{
                  fontSize: '0.72rem', letterSpacing: '0.1em',
                  color: '#558b2f', textTransform: 'uppercase',
                  fontFamily: "'Outfit', sans-serif", fontWeight: 600, mt: 0.8,
                }}>
                  {s.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════
          WHAT IS FENAGRO — clean white, fresh
      ═══════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 14 }, background: '#ffffff' }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: '#2e7d32', mb: 2, display: 'block' }}>
                ¿Quiénes somos?
              </Typography>
              <Typography variant="h2" sx={{
                color: '#1a3a1e', mb: 3,
                fontSize: { xs: '1.9rem', md: '2.7rem' },
              }}>
                Una Red que
                <Box component="span" sx={{ color: '#2e7d32', fontStyle: 'italic' }}> transforma</Box>
                <br />el campo
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a6741', mb: 3, lineHeight: 1.8 }}>
                <strong>FENAGRO APL</strong> es una Red Empresarial Socio-Económica Colaborativa de segundo nivel
                perteneciente al sector solidario. Fortalece la responsabilidad social empresarial — económica,
                educativa, ambiental y político-privada — de sus organizaciones asociadas.
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a6741', mb: 4, lineHeight: 1.8 }}>
                Fundada en 2016 por un grupo de profesionales convencidos de la importancia de la biodiversidad
                y riqueza agropecuaria colombiana, articula redes de personas, centros de emprendimiento,
                bancos de proyectos y economía colaborativa.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 5 }}>
                {[
                  'Escuela de Emprendimiento Agro-industrial APL',
                  'Modelo Empresarial Agroindustrial Asociativo',
                  'Mercado asegurado de los productos',
                  'Banca de Emprendimiento Agroindustrial',
                ].map((pilar, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar sx={{
                      width: 28, height: 28,
                      background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                      fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                    }}>
                      {i + 1}
                    </Avatar>
                    <Typography variant="body2" sx={{ color: '#2d4a2f', fontWeight: 500, pt: 0.5, lineHeight: 1.5 }}>
                      {pilar}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                component={Link}
                to="/nosotros"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                  borderRadius: 3, px: 3, py: 1.4,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(46,125,50,0.3)',
                  },
                }}
              >
                Nuestra Historia
              </Button>
            </Grid>

            <Grid item xs={12} md={7}>
              {/* Fresh value-proposition cards */}
              <Grid container spacing={2.5}>
                {[
                  {
                    icon: <ForestIcon sx={{ fontSize: 28 }} />,
                    title: 'Campo Sostenible',
                    desc: 'Promovemos la producción limpia y orgánica con protocolos BPA, ICA e INVIMA para un agro más sano.',
                    bg: '#e8f5e9', iconBg: '#2e7d32', accent: '#2e7d32',
                  },
                  {
                    icon: <VolunteerActivismIcon sx={{ fontSize: 28 }} />,
                    title: 'Solidaridad Real',
                    desc: 'La ayuda mutua y la cooperación son nuestro motor. Juntos somos más fuertes que individualmente.',
                    bg: '#f1f8e9', iconBg: '#558b2f', accent: '#558b2f',
                  },
                  {
                    icon: <WbSunnyIcon sx={{ fontSize: 28 }} />,
                    title: 'Futuro Optimista',
                    desc: 'Conectamos el campo colombiano con mercados justos, tecnología moderna y oportunidades reales.',
                    bg: '#fff9e6', iconBg: '#f9a825', accent: '#e65100',
                  },
                  {
                    icon: <GroupsIcon sx={{ fontSize: 28 }} />,
                    title: 'Red Colaborativa',
                    desc: 'Miles de productores, cooperativas y emprendedores unidos bajo un mismo propósito colectivo.',
                    bg: '#e3f2fd', iconBg: '#0277bd', accent: '#0277bd',
                  },
                ].map((card, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box sx={{
                      p: 3.5, borderRadius: 4,
                      background: card.bg,
                      border: `1px solid ${card.accent}18`,
                      display: 'flex', flexDirection: 'column', gap: 1.5,
                      transition: 'all 0.3s ease',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 40px ${card.accent}22`,
                      },
                    }}>
                      <Box sx={{
                        width: 52, height: 52, borderRadius: 3,
                        background: card.iconBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '& svg': { color: '#ffffff' },
                      }}>
                        {card.icon}
                      </Box>
                      <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: '1.1rem', color: '#1a3a1e' }}>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4a6741', lineHeight: 1.7 }}>
                        {card.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════
          INSPIRATIONAL QUOTE — warm light green
      ═══════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 50%, #f9fbe7 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative leaf shapes */}
        <Box sx={{
          position: 'absolute', top: -60, right: -60,
          width: 280, height: 280, borderRadius: '60% 40% 70% 30% / 40% 60% 30% 70%',
          background: 'rgba(46,125,50,0.06)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -40, left: -40,
          width: 200, height: 200, borderRadius: '40% 60% 30% 70% / 60% 40% 70% 30%',
          background: 'rgba(85,139,47,0.06)',
          pointerEvents: 'none',
        }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography sx={{
            fontFamily: "'Playfair Display', serif",
            fontSize: { xs: '4rem', md: '7rem' },
            color: 'rgba(46,125,50,0.15)', lineHeight: 0.6, mb: 2,
            userSelect: 'none',
          }}>
            "
          </Typography>
          <Typography sx={{
            fontFamily: "'Playfair Display', serif",
            color: '#1a3a1e',
            fontSize: { xs: '1.15rem', md: '1.5rem' },
            lineHeight: 1.65, mb: 3,
            fontStyle: 'italic',
          }}>
            El éxito empresarial no depende solo de lo que conoces, sino de a quién conoces y cómo te das a conocer.
            No depende de lo que haces, sino de <em>cómo</em> lo haces.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.2 }}>
            {['Marketing Digital', 'Redes Empresariales', 'Emprendimiento', 'Innovación Tecnológica'].map(tag => (
              <Chip
                key={tag}
                label={tag}
                sx={{
                  background: 'rgba(46,125,50,0.10)',
                  color: '#2e7d32', border: '1px solid rgba(46,125,50,0.2)',
                  fontWeight: 600, fontSize: '0.78rem',
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════
          SERVICES — white, card grid
      ═══════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 14 }, background: '#ffffff' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: '#2e7d32', mb: 1, display: 'block' }}>
              Nuestro portafolio
            </Typography>
            <Typography variant="h2" sx={{ color: '#1a3a1e', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              Bienes & Servicios
            </Typography>
            <Divider sx={{ width: 56, mx: 'auto', my: 2.5, borderColor: '#43a047', borderBottomWidth: 3, borderRadius: 2 }} />
            <Typography variant="body1" sx={{ color: '#4a6741', maxWidth: 580, mx: 'auto' }}>
              Un ecosistema completo de soluciones para el fortalecimiento del sector agroindustrial y solidario colombiano.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {services.map((service, i) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={i}>
                <Card
                  component={Link}
                  to={service.path}
                  sx={{
                    height: '100%', textDecoration: 'none',
                    display: 'flex', flexDirection: 'column',
                    border: '1px solid rgba(0,0,0,0.06)',
                    background: '#ffffff',
                    '&:hover': {
                      '& .s-icon': {
                        background: service.color,
                        '& svg': { color: '#fff' },
                      },
                      '& .s-title': { color: service.color },
                      borderColor: `${service.color}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3.5, flexGrow: 1 }}>
                    <Box
                      className="s-icon"
                      sx={{
                        width: 50, height: 50, borderRadius: 2, mb: 2.5,
                        background: `${service.color}14`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        '& svg': { color: service.color, fontSize: 24, transition: 'color 0.3s ease' },
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      className="s-title"
                      variant="h6"
                      sx={{ mb: 1.5, fontSize: '0.98rem', color: '#1a3a1e', transition: 'color 0.3s ease' }}
                    >
                      {service.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b8f71', lineHeight: 1.65 }}>
                      {service.desc}
                    </Typography>
                  </CardContent>
                  <Box sx={{
                    px: 3.5, pb: 3,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    color: service.color, fontSize: '0.76rem',
                    fontFamily: "'Outfit', sans-serif", fontWeight: 700,
                    letterSpacing: '0.05em', textTransform: 'uppercase',
                  }}>
                    Ver más <ArrowForwardIcon sx={{ fontSize: 13 }} />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════
          CTA — photo background, fresh green overlay
      ═══════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 10, md: 16 },
        position: 'relative', overflow: 'hidden',
        textAlign: 'center',
        backgroundImage: `url(${hero3})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        {/* Luminous green overlay — NOT dark */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(27,94,32,0.78) 0%, rgba(46,125,50,0.65) 50%, rgba(85,139,47,0.70) 100%)',
        }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" sx={{ color: '#a5d6a7', mb: 2, display: 'block' }}>
            Únete a la Red
          </Typography>
          <Typography variant="h2" sx={{
            color: '#ffffff', mb: 3,
            fontSize: { xs: '2rem', md: '3rem' },
            textShadow: '0 3px 20px rgba(0,0,0,0.3)',
          }}>
            Transforma tu futuro<br />con FENAGRO APL
          </Typography>
          <Typography variant="body1" sx={{
            color: 'rgba(255,255,255,0.85)', mb: 5,
            maxWidth: 540, mx: 'auto', fontSize: '1.05rem',
          }}>
            Forma parte de la red solidaria que está cambiando el paradigma del sector agroindustrial colombiano.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/afiliacion"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: '#ffffff', color: '#2e7d32',
                fontWeight: 700, px: 5, py: 1.8, borderRadius: 3,
                boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
                '&:hover': { background: '#f1f8e9', transform: 'translateY(-2px)', boxShadow: '0 10px 32px rgba(0,0,0,0.25)' },
              }}
            >
              Afiliarme Ahora
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/contacto"
              sx={{
                borderColor: 'rgba(255,255,255,0.65)', color: '#ffffff',
                px: 5, py: 1.8, borderRadius: 3, borderWidth: 2,
                backdropFilter: 'blur(6px)',
                background: 'rgba(255,255,255,0.08)',
                '&:hover': { borderColor: '#a5d6a7', color: '#a5d6a7', borderWidth: 2 },
              }}
            >
              Contáctenos
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
